import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import ReactApexChart from "react-apexcharts";
import useMediaQuery from "@mui/material/useMediaQuery";

function readCssVar(name) {
  if (typeof window === "undefined") return "";
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function resolveColor(c) {
  if (typeof c === "string" && c.startsWith("var(")) {
    const name = c.slice(4, -1).trim();
    return readCssVar(name) || "#10b5a0";
  }
  return c;
}

function readTheme() {
  if (typeof document === "undefined") return "light";
  return document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
}

function toMs(t) {
  if (t == null) return null;
  if (typeof t === "number") return t;
  const ms = new Date(t).getTime();
  return Number.isNaN(ms) ? null : ms;
}

export default function LineChart({
  series,
  labels,
  timestamps,
  height,
  unit,
  type,
  curve,
  xTitle,
  yTitle,
  yTitleRight,
  yMin,
  yMax,
  yMinRight,
  yMaxRight,
  showLegend,
  stacked,
}) {
  const [, force] = useState(0);
  useEffect(() => {
    const on = () => force((v) => v + 1);
    const observer = new MutationObserver(on);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  // On phones the plot is narrow: thin the x-axis to fewer, steeper labels so
  // the (long) date labels stop overlapping.
  const isNarrow = useMediaQuery("(max-width: 575.98px)");

  const themeMode = readTheme();
  const colors = useMemo(
    () => series.map((s) => resolveColor(s.color || "var(--accent)")),
    [series]
  );
  const grid = readCssVar("--grid-line") || "#eef2f5";
  const ink3 = readCssVar("--ink-3") || "#8696a0";
  const sans = readCssVar("--sans") || "Roboto, sans-serif";

  // Y axis title defaults to the unit (e.g. "kWh"); X is opt-in per chart.
  const yAxisTitle = yTitle != null ? yTitle : (unit || "").trim();
  const axisTitleStyle = { color: ink3, fontSize: "11.5px", fontWeight: 600, fontFamily: sans };

  // Optional secondary (right) y-axis: series tagged `axis: "right"` are plotted
  // against an independent scale so e.g. price (€/kWh) lines stay readable next
  // to energy (kWh) — instead of flattening to ~0 on a shared axis.
  const rightNames = useMemo(
    () => series.filter((s) => s.axis === "right").map((s) => s.name),
    [series]
  );
  const leftNames = useMemo(
    () => series.filter((s) => s.axis !== "right").map((s) => s.name),
    [series]
  );
  const hasRightAxis = rightNames.length > 0 && leftNames.length > 0;
  const numFmt = (digits) => (v) => {
    if (v == null || Number.isNaN(v)) return "";
    return Number(v).toLocaleString(undefined, { maximumFractionDigits: digits });
  };

  // Mixed bar/line: when any series declares its own `type`, render a combo chart
  // (e.g. net load as columns + price lines). stroke width / fill opacity then
  // become per-series arrays so columns stay filled while lines stay stroked.
  const baseType = type === "bar" ? "column" : type;
  const isMixed = useMemo(() => series.some((s) => s.type), [series]);
  const seriesTypes = useMemo(() => series.map((s) => s.type || baseType), [series, baseType]);
  const isLineLike = (tp) => tp === "line" || tp === "area";
  const strokeWidth = useMemo(() => {
    if (!isMixed) return type === "bar" ? 0 : 2.6;
    // Per-series width: 0 for columns (no border), 2.6 for the lines.
    return seriesTypes.map((tp) => (isLineLike(tp) ? 2.6 : 0));
  }, [isMixed, seriesTypes, type]);
  const fillOpacity = useMemo(() => {
    // eslint-disable-next-line no-nested-ternary
    if (!isMixed) return type === "area" ? 0.1 : type === "bar" ? 0.9 : 0;
    // IMPORTANT: in a combo chart ApexCharts derives each LINE series' stroke
    // alpha from its fill opacity — a 0 here makes the line invisible (rgba(..,0)),
    // even though it's drawn. Lines must therefore use opacity 1 (they have no area
    // fill anyway as type "line"); areas keep a light 0.1, columns 0.9.
    // eslint-disable-next-line no-nested-ternary
    return seriesTypes.map((tp) => (tp === "area" ? 0.1 : tp === "line" ? 1 : 0.9));
  }, [isMixed, seriesTypes, type]);

  // When real timestamps are supplied, use a datetime x-axis so ApexCharts
  // auto-spaces/auto-formats the ticks (few, readable labels) instead of
  // cramming one label per category.
  const tsMs = useMemo(() => (timestamps || []).map(toMs), [timestamps]);
  // Only switch to the datetime axis when the timestamps line up with the data
  // and there are at least two finite values (a single/empty/NaN set makes
  // ApexCharts compute NaN tick positions).
  const finiteTsCount = useMemo(() => tsMs.filter((v) => Number.isFinite(v)).length, [tsMs]);
  const useTimeAxis = tsMs.length === series[0]?.data?.length && finiteTsCount >= 2;

  const options = useMemo(
    () => ({
      chart: {
        type,
        height,
        width: "100%",
        stacked,
        fontFamily: sans,
        toolbar: { show: false },
        zoom: { enabled: false },
        parentHeightOffset: 0,
        background: "transparent",
        animations: { enabled: true, speed: 450, easing: "easeinout" },
      },
      theme: { mode: themeMode },
      colors,
      // bar settings verbatim from the design prototype's AreaChart (charts.jsx)
      plotOptions: { bar: { columnWidth: "58%", borderRadius: 3, borderRadiusApplication: "end" } },
      stroke: {
        curve,
        width: strokeWidth,
        lineCap: type === "bar" ? "butt" : "round",
      },
      fill: { type: "solid", opacity: fillOpacity },
      dataLabels: { enabled: false },
      grid: {
        borderColor: grid,
        strokeDashArray: 0,
        padding: { left: 8, right: 12, top: 4, bottom: 0 },
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
      },
      xaxis: useTimeAxis
        ? {
            type: "datetime",
            // No tickAmount: a datetime axis auto-spaces/auto-thins its ticks by
            // available width. Forcing tickAmount here triggers NaN tick maths.
            tickPlacement: type === "bar" ? "between" : "on",
            axisBorder: { show: false },
            axisTicks: { show: false },
            title: { text: xTitle || undefined, style: axisTitleStyle },
            labels: {
              datetimeUTC: false,
              rotate: isNarrow ? -45 : 0,
              hideOverlappingLabels: true,
              style: { colors: ink3, fontSize: "11px", fontFamily: sans },
              datetimeFormatter: {
                year: "yyyy",
                month: "MMM 'yy",
                day: "dd MMM",
                hour: "HH:mm",
              },
            },
          }
        : {
            categories: labels,
            tickPlacement: type === "bar" ? "between" : "on",
            // eslint-disable-next-line no-nested-ternary
            tickAmount: isNarrow
              ? labels.length > 5
                ? 5
                : undefined
              : labels.length > 10
              ? 10
              : undefined,
            axisBorder: { show: false },
            axisTicks: { show: false },
            // Always an object (ApexCharts reads title.text); empty → no title shown.
            title: { text: xTitle || undefined, style: axisTitleStyle },
            labels: {
              style: { colors: ink3, fontSize: "11px", fontFamily: sans },
              rotate: isNarrow ? -45 : -30,
              rotateAlways: false,
              hideOverlappingLabels: true,
              trim: false,
            },
          },
      yaxis: hasRightAxis
        ? // Exactly TWO visible axes — left for the default series, right
          // (opposite) for every series tagged axis:"right", grouped via an array
          // seriesName. We deliberately avoid one-axis-per-series with show:false:
          // a hidden y-axis among several makes ApexCharts compute a NaN axis width
          // ("translate(NaN,0)") and break the whole plot.
          [
            {
              seriesName: leftNames,
              tickAmount: 5,
              min: yMin,
              max: yMax,
              title: { text: yAxisTitle || undefined, style: axisTitleStyle },
              labels: {
                style: { colors: ink3, fontSize: "11px", fontFamily: sans },
                formatter: numFmt(2),
              },
            },
            {
              seriesName: rightNames,
              opposite: true,
              tickAmount: 5,
              min: yMinRight,
              max: yMaxRight,
              title: { text: yTitleRight || undefined, style: axisTitleStyle },
              labels: {
                style: { colors: ink3, fontSize: "11px", fontFamily: sans },
                // When the right axis is given a negative floor purely to align its
                // zero with the left axis, hide the (meaningless) negative ticks.
                formatter: (v) => (yMinRight != null && yMinRight < 0 && v < 0 ? "" : numFmt(3)(v)),
              },
            },
          ]
        : {
            tickAmount: 5,
            min: yMin,
            max: yMax,
            title: { text: yAxisTitle || undefined, style: axisTitleStyle },
            labels: {
              style: { colors: ink3, fontSize: "11px", fontFamily: sans },
              formatter: numFmt(2),
            },
          },
      tooltip: {
        theme: themeMode,
        // Only override `x` for the datetime axis. Setting it to `undefined`
        // makes ApexCharts crash reading `tooltip.x.show`; omit the key instead.
        ...(useTimeAxis ? { x: { format: "dd MMM yyyy" } } : {}),
        y: {
          formatter: (v) => {
            if (v == null || Number.isNaN(v)) return "";
            const num = Number(v);
            const formatted =
              Math.abs(num) >= 1000
                ? num.toLocaleString(undefined, { maximumFractionDigits: 0 })
                : num.toLocaleString(undefined, { maximumFractionDigits: 2 });
            return `${formatted}${unit ? ` ${unit}` : ""}`;
          },
        },
      },
      legend: { show: showLegend && series.length > 1, labels: { colors: ink3 } },
    }),
    [
      type,
      curve,
      height,
      sans,
      themeMode,
      colors,
      grid,
      ink3,
      useTimeAxis,
      tsMs,
      labels,
      xTitle,
      yAxisTitle,
      yTitleRight,
      yMin,
      yMax,
      yMinRight,
      yMaxRight,
      strokeWidth,
      fillOpacity,
      hasRightAxis,
      leftNames,
      rightNames,
      unit,
      showLegend,
      stacked,
      series.length,
      isNarrow,
    ]
  );

  const apexSeries = useMemo(
    () =>
      series.map((s, i) => ({
        name: s.name,
        ...(isMixed ? { type: seriesTypes[i] } : {}),
        data: useTimeAxis
          ? s.data.map((y, i2) => ({ x: tsMs[i2], y })).filter((p) => Number.isFinite(p.x))
          : s.data,
      })),
    [series, isMixed, seriesTypes, useTimeAxis, tsMs]
  );

  // react-apexcharts does not reliably apply a changed chart `type`, colors,
  // grid borderColor — or a swapped dataset (esp. on a datetime axis, where the
  // line can fail to redraw) — to an existing instance. Remount when the shape,
  // theme, curve, or the data signature (length + first/last x) changes so the
  // tokens are re-read and the series is drawn fresh.
  const firstX = useTimeAxis ? tsMs[0] : labels[0];
  const lastX = useTimeAxis ? tsMs[tsMs.length - 1] : labels[labels.length - 1];
  const chartKey = `${type}-${curve}-${stacked ? "s" : ""}-${themeMode}-${
    useTimeAxis ? "t" : "c"
  }-${isNarrow ? "n" : "w"}-${series.map((s) => s.name).join("|")}-${
    series[0]?.data?.length ?? 0
  }-${firstX ?? ""}-${lastX ?? ""}`;

  return (
    <ReactApexChart
      key={chartKey}
      options={options}
      series={apexSeries}
      type={type}
      height={height}
    />
  );
}

LineChart.propTypes = {
  series: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      data: PropTypes.arrayOf(PropTypes.number).isRequired,
      color: PropTypes.string,
      axis: PropTypes.oneOf(["left", "right"]),
      // Per-series chart type for combo (mixed) charts, e.g. "column" + "line".
      type: PropTypes.oneOf(["line", "area", "column", "bar"]),
    })
  ).isRequired,
  labels: PropTypes.arrayOf(PropTypes.string),
  timestamps: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  height: PropTypes.number,
  unit: PropTypes.string,
  type: PropTypes.oneOf(["area", "line", "bar"]),
  curve: PropTypes.oneOf(["smooth", "straight", "stepline"]),
  xTitle: PropTypes.string,
  yTitle: PropTypes.string,
  yTitleRight: PropTypes.string,
  yMin: PropTypes.number,
  yMax: PropTypes.number,
  yMinRight: PropTypes.number,
  yMaxRight: PropTypes.number,
  showLegend: PropTypes.bool,
  stacked: PropTypes.bool,
};

LineChart.defaultProps = {
  labels: [],
  timestamps: undefined,
  height: 300,
  unit: "",
  type: "area",
  curve: "smooth",
  xTitle: undefined,
  yTitle: undefined,
  yTitleRight: undefined,
  yMin: undefined,
  yMax: undefined,
  yMinRight: undefined,
  yMaxRight: undefined,
  showLegend: true,
  stacked: false,
};
